/**
 * Unified Compliance Reports Component
 * Handles all report views: list, templates, builder, viewer, editor
 * Loads templates dynamically from policy packs or fallback configs
 */

import React, { useState, useEffect } from 'react';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { ReportsList } from './ReportsList';
import { ReportViewer } from './ReportViewer';
import { ReportEditor } from './ReportEditor';
import { ReportBuilder } from './ReportBuilder';
import { ReportTemplateSelector } from './ReportTemplateSelector';
import { ReportBuilderForm } from './ReportBuilderForm';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useReportJobSubmit } from '../../hooks/useReportJob';
import { useReportTemplates } from '../../hooks/useReportTemplates';
import { ReportTemplate } from '../../types/reports';
import { FileText, ArrowLeft, Plus } from 'lucide-react';

type View = 'list' | 'templates' | 'builder' | 'viewer' | 'editor';

interface ComplianceReportsProps {
  fallbackTemplates?: ReportTemplate[];
  title?: string;
  description?: string;
  onReportTypeMapper?: (frontendType: string) => string; // Maps frontend type to backend enum
  initialReportId?: string; // Optional report ID to open in viewer on mount
  hideHeader?: boolean; // Hide header when viewing a specific report
  reportsQueryKeySuffix?: string; // Optional suffix for reports query key (e.g., 'rfp_proposals')
  listReportsOptions?: { exclude_report_type?: string; report_type?: string; status?: string; source_type?: string }; // Options to pass to listReports
}

interface State {
  view: View;
  selectedReportId?: string;
  selectedTemplate?: ReportTemplate;
}

export function ComplianceReports({ 
  fallbackTemplates = [],
  title = 'Reports',
  description = 'View and manage all reports across projects',
  onReportTypeMapper,
  initialReportId,
  hideHeader = false,
  reportsQueryKeySuffix,
  listReportsOptions,
}: ComplianceReportsProps) {
  const { projectId } = useWorkspace();
  const [state, setState] = useState<State>({ 
    view: initialReportId ? 'viewer' : 'list',
    selectedReportId: initialReportId,
  });
  const submitMutation = useReportJobSubmit();
  
  // Handle initialReportId prop changes
  useEffect(() => {
    if (initialReportId && initialReportId !== state.selectedReportId) {
      setState({ view: 'viewer', selectedReportId: initialReportId });
    }
  }, [initialReportId]);
  
  // Load templates from policy pack or fallback
  const { templates, reportTypes, isLoading: templatesLoading } = useReportTemplates({
    fallbackTemplates,
  });

  const handleSelectTemplate = (template: ReportTemplate) => {
    setState({ view: 'builder', selectedTemplate: template });
  };

  const handleCustomReport = () => {
    setState({ view: 'builder', selectedTemplate: undefined });
  };

  const handleViewReport = (reportId: string) => {
    setState({ 
      view: 'viewer', 
      selectedReportId: reportId 
    });
  };

  const handleEditReport = () => {
    if (state.selectedReportId) {
      setState({ view: 'editor', selectedReportId: state.selectedReportId });
    }
  };

  const handleGenerateReport = async (clientName: string, questions: any[]) => {
    if (!projectId || !state.selectedTemplate) return;

    try {
      console.log('[ComplianceReports] Generating report:', {
        project_id: projectId,
        report_type: state.selectedTemplate.type,
        client_name: clientName,
        questions_count: questions.length,
      });

      // Map frontend type to backend enum if mapper provided
      const backendType = onReportTypeMapper 
        ? onReportTypeMapper(state.selectedTemplate.type)
        : 'compliance'; // Default to compliance
      
      const jobStatus = await submitMutation.mutateAsync({
        project_id: projectId,
        report_type: backendType,
        client_name: clientName,
        questions: questions,
        enable_cache: true,
        metadata: {
          frontend_type: state.selectedTemplate.type,
          template_id: state.selectedTemplate.id,
          template_name: state.selectedTemplate.name,
          regulatory_context: state.selectedTemplate.regulatoryContext,
        },
      });
      
      console.log('[ComplianceReports] Report job submitted:', jobStatus);
      
      // Navigate to list view to see the new report
      setState({ view: 'list' });
    } catch (error: any) {
      console.error('[ComplianceReports] Failed to generate report:', error);
      const validationErrors = error.response?.data?.detail;
      const errorMessage = validationErrors 
        ? `Validation failed: ${JSON.stringify(validationErrors, null, 2)}`
        : error.message;
      
      alert(`Failed to generate report:\n${errorMessage}`);
    }
  };

  const handleBack = () => {
    if (state.view === 'builder' || state.view === 'templates') {
      setState({ view: 'list' });
    } else if (state.view === 'viewer' || state.view === 'editor') {
      setState({ view: 'list' });
    }
  };

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500 dark:text-gray-300">
          Select a project to access reports
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Hide when hideHeader is true and viewing a report */}
      {!(hideHeader && (state.view === 'viewer' || state.view === 'editor')) && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {state.view !== 'list' && (
              <button
                onClick={handleBack}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
              </button>
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <FileText className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                {title}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {state.view === 'list' && description}
                {state.view === 'templates' && 'Choose a report template'}
                {state.view === 'builder' && 'Configure and generate report'}
                {state.view === 'viewer' && 'View report details'}
                {state.view === 'editor' && 'Edit report content'}
              </p>
            </div>
          </div>
          
          {state.view === 'list' && (
            <Button
              onClick={() => setState({ view: 'templates' })}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              New Report
            </Button>
          )}
        </div>
      )}

      {/* Content based on view */}
      {state.view === 'list' && (
        <ReportsList
          onViewReport={handleViewReport}
          queryKeySuffix={reportsQueryKeySuffix}
          listReportsOptions={listReportsOptions}
          onCreateNew={() => setState({ view: 'templates' })}
        />
      )}

      {state.view === 'templates' && (
        <ReportTemplateSelector
          templates={templates}
          onSelectTemplate={handleSelectTemplate}
          onCustomReport={handleCustomReport}
          title="Choose a Report Template"
          description="Select a pre-configured template or build a custom report from scratch"
        />
      )}

      {state.view === 'builder' && (
        <>
          {!state.selectedTemplate ? (
            <ReportBuilder
              availableTypes={reportTypes}
              onComplete={(reportId) => {
                console.log('[ComplianceReports] Report complete:', reportId);
                if (reportId) {
                  setState({ view: 'viewer', selectedReportId: reportId });
                } else {
                  setState({ view: 'list' });
                }
              }}
            />
          ) : (
            <ReportBuilderForm
              template={state.selectedTemplate}
              onGenerate={handleGenerateReport}
              onCancel={handleBack}
              isLoading={submitMutation.isPending}
            />
          )}
        </>
      )}

      {state.view === 'viewer' && state.selectedReportId && (
        <ReportViewer
          reportId={state.selectedReportId}
          onEdit={handleEditReport}
          onClose={() => setState({ view: 'list' })}
        />
      )}

      {state.view === 'editor' && state.selectedReportId && (
        <ReportEditor
          reportId={state.selectedReportId}
          onSave={() => setState({ view: 'viewer', selectedReportId: state.selectedReportId })}
          onCancel={() => setState({ view: 'viewer', selectedReportId: state.selectedReportId })}
        />
      )}
    </div>
  );
}

